import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Truck,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  Pencil,
  X,
  Save,
  AlertTriangle,
} from 'lucide-react';
import MainService from '../../services/MainService';
import {
  normalizeVehicleGridResponse,
  mapVehicleDropdownList,
  isApiSaveSuccess,
  isApiDeleteSuccess,
} from '../../utils/vehicleApiUtils';

const defaultPageSize = 20;

function callService(methodName, ...args) {
  const fn = MainService[methodName];
  if (typeof fn !== 'function') {
    return Promise.reject(new Error(`MainService.${methodName} is not defined`));
  }
  return fn(...args);
}

const VehicleEntityGridPage = ({ config }) => {
  const { vehicleId: routeVehicleId } = useParams();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(defaultPageSize);
  const [totalCount, setTotalCount] = useState(0);
  const [keyword, setKeyword] = useState('');
  const [vehicleFilter, setVehicleFilter] = useState(routeVehicleId || '');
  const [vehicles, setVehicles] = useState([]);
  const [expenseOptions, setExpenseOptions] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(0);
  const [form, setForm] = useState({});

  const effectiveVehicleId = routeVehicleId || vehicleFilter || null;

  const loadVehicles = useCallback(async () => {
    try {
      const list = await MainService.VehiclesInDropDown();
      setVehicles(mapVehicleDropdownList(list));
    } catch (e) {
      console.error(e);
      setVehicles([]);
    }
  }, []);

  const loadExpenses = useCallback(async () => {
    if (!config.expenseDropdown) return;
    try {
      const res = await MainService.ExpensesInGrid({
        Page: 1,
        PageSize: 500,
        Keyword: '',
      });
      const { data } = normalizeVehicleGridResponse(res);
      setExpenseOptions(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setExpenseOptions([]);
    }
  }, [config.expenseDropdown]);

  const loadGrid = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const body = {
        Page: page,
        PageSize: pageSize,
        Keyword: keyword || '',
      };
      if (effectiveVehicleId) {
        body.VehicleId = Number(effectiveVehicleId);
      }
      const res = await callService(config.gridKey, body);
      const { data, totalCount } = normalizeVehicleGridResponse(res);
      setRows(Array.isArray(data) ? data : []);
      setTotalCount(totalCount);
    } catch (e) {
      console.error(e);
      setError(e.message || 'Failed to load data');
      setRows([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [config.gridKey, page, pageSize, keyword, effectiveVehicleId]);

  useEffect(() => {
    loadVehicles();
    loadExpenses();
  }, [loadVehicles, loadExpenses]);

  useEffect(() => {
    loadGrid();
  }, [loadGrid]);

  useEffect(() => {
    if (routeVehicleId) {
      setVehicleFilter(routeVehicleId);
    }
  }, [routeVehicleId]);

  const openAdd = () => {
    const initial = { VehicleId: effectiveVehicleId || '' };
    if (config.expenseDropdown) {
      initial.ExpenseId = '';
    }
    (config.formFields || []).forEach((f) => {
      initial[f.name] = f.type === 'checkbox' ? false : '';
    });
    setEditingId(0);
    setForm(initial);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    const next = {
      VehicleId: row.VehicleId ?? effectiveVehicleId ?? '',
    };
    if (config.expenseDropdown) {
      next.ExpenseId = row.ExpenseId ?? row.ExpenseID ?? '';
    }
    (config.formFields || []).forEach((f) => {
      const v = row[f.name];
      if (f.type === 'checkbox') next[f.name] = !!v;
      else next[f.name] = v ?? '';
    });
    setEditingId(row.Id ?? row.ID ?? 0);
    setForm(next);
    setModalOpen(true);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSave = async () => {
    const vid = Number(form.VehicleId || effectiveVehicleId);
    if (!vid) {
      setError('Select a vehicle');
      return;
    }
    if (config.expenseDropdown && !Number(form.ExpenseId)) {
      setError('Select an expense type');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const dto = {
        Id: editingId,
        VehicleId: vid,
      };
      (config.formFields || []).forEach((f) => {
        const raw = form[f.name];
        if (f.type === 'checkbox') {
          dto[f.name] = !!raw;
        } else if (f.name === 'DriverId' && (raw === '' || raw == null)) {
          dto.DriverId = null;
        } else if (
          f.type === 'number' ||
          ['Amount', 'Cost', 'PremiumAmount', 'CoverageAmount', 'Liters'].includes(f.name)
        ) {
          if (raw === '' || raw == null) dto[f.name] = f.nullable ? null : 0;
          else dto[f.name] = Number(raw);
        } else {
          dto[f.name] = raw;
        }
      });
      if (config.expenseDropdown) {
        dto.ExpenseId = Number(form.ExpenseId);
      }
      const saveRes = await callService(config.saveKey, dto);
      if (!isApiSaveSuccess(saveRes)) {
        setError('Save failed');
        return;
      }
      setModalOpen(false);
      await loadGrid();
    } catch (e) {
      console.error(e);
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    const id = row.Id ?? row.ID;
    if (!id || !window.confirm('Delete this record?')) return;
    try {
      const delRes = await callService(config.deleteKey, id);
      if (!isApiDeleteSuccess(delRes)) {
        setError(typeof delRes === 'string' ? delRes : 'Delete failed');
        return;
      }
      await loadGrid();
    } catch (e) {
      console.error(e);
      setError(e.message || 'Delete failed');
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-[#f0f9f4] to-slate-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200/60 p-6 md:p-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-[#46955A] rounded-xl shadow-lg">
                <Truck className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-slate-900">
                  {config.title}
                </h1>
                {config.subtitle && (
                  <p className="text-slate-600 mt-1">{config.subtitle}</p>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => navigate('/home/vehicle-management/vehicle-list')}
                className="px-4 py-2 rounded-lg border border-[#46955A] text-[#46955A] hover:bg-[#f0f9f4]"
              >
                Back to vehicles
              </button>
              <button
                type="button"
                onClick={openAdd}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#46955A] text-white shadow-md hover:bg-[#3d7d4f]"
              >
                <Plus className="w-4 h-4" />
                Add
              </button>
              <button
                type="button"
                onClick={loadGrid}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[#46955A] text-[#46955A] hover:bg-[#f0f9f4]"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>

          {error && (
            <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-800">
              <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <div className="flex flex-col md:flex-row gap-3 mb-4">
            {!routeVehicleId && (
              <div className="flex-1 min-w-[200px]">
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Vehicle
                </label>
                <select
                  value={vehicleFilter}
                  onChange={(e) => {
                    setVehicleFilter(e.target.value);
                    setPage(1);
                  }}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                >
                  <option value="">All vehicles</option>
                  {vehicles.map((v) => (
                    <option key={v.Id ?? v.id} value={v.Id ?? v.id}>
                      {v.PlateNumber || v.Name || v.Text || v.Id}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-medium text-slate-500 mb-1">
                Search
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#46955A]" />
                <input
                  type="text"
                  value={keyword}
                  onChange={(e) => {
                    setKeyword(e.target.value);
                    setPage(1);
                  }}
                  placeholder="Keyword..."
                  className="w-full rounded-lg border border-slate-200 pl-9 pr-3 py-2 text-sm focus:border-[#46955A] focus:ring-1 focus:ring-[#46955A]/30 focus:outline-none"
                />
              </div>
            </div>
          </div>

          <div className="overflow-x-auto rounded-xl border border-slate-200">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-slate-700">
                <tr>
                  {(config.columns || []).map((c) => (
                    <th
                      key={c.key}
                      className="text-left font-semibold px-4 py-3 whitespace-nowrap"
                    >
                      {c.header}
                    </th>
                  ))}
                  <th className="text-right font-semibold px-4 py-3 w-24">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={(config.columns?.length || 0) + 1}
                      className="px-4 py-8 text-center text-slate-500"
                    >
                      Loading...
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={(config.columns?.length || 0) + 1}
                      className="px-4 py-8 text-center text-slate-500"
                    >
                      No records found.
                    </td>
                  </tr>
                ) : (
                  rows.map((row, idx) => (
                    <tr
                      key={row.Id ?? row.ID ?? idx}
                      className="border-t border-slate-100 hover:bg-slate-50/80"
                    >
                      {(config.columns || []).map((c) => (
                        <td key={c.key} className="px-4 py-3 text-slate-800 whitespace-nowrap">
                          {c.render ? c.render(row) : row[c.key] ?? '—'}
                        </td>
                      ))}
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <div className="inline-flex items-center justify-end gap-1.5">
                          <button
                            type="button"
                            onClick={() => openEdit(row)}
                            title="Edit"
                            aria-label="Edit"
                            className="inline-flex items-center justify-center p-2 rounded-lg bg-[#46955A] text-white hover:bg-[#3d7d4f] transition-colors"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(row)}
                            title="Delete"
                            aria-label="Delete"
                            className="inline-flex items-center justify-center p-2 rounded-lg bg-[#46955A] text-white hover:bg-[#3d7d4f] transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between mt-4 text-sm text-slate-600">
            <span>
              Page {page} of {totalPages} · {totalCount} records
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-3 py-1 rounded-lg border border-[#46955A] text-[#46955A] hover:bg-[#f0f9f4] disabled:opacity-40"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="px-3 py-1 rounded-lg border border-[#46955A] text-[#46955A] hover:bg-[#f0f9f4] disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h2 className="text-lg font-semibold text-slate-900">
                {editingId ? 'Edit record' : 'Add record'}
              </h2>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="p-2 rounded-lg text-[#46955A] hover:bg-[#f0f9f4]"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Vehicle *
                </label>
                <select
                  name="VehicleId"
                  value={form.VehicleId ?? ''}
                  onChange={handleFormChange}
                  disabled={!!routeVehicleId}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm disabled:bg-slate-50"
                >
                  <option value="">Select vehicle</option>
                  {vehicles.map((v) => (
                    <option key={v.Id ?? v.id} value={v.Id ?? v.id}>
                      {v.PlateNumber || v.Name || v.Text || v.Id}
                    </option>
                  ))}
                </select>
              </div>
              {config.expenseDropdown && (
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">
                    Expense *
                  </label>
                  <select
                    name="ExpenseId"
                    value={form.ExpenseId ?? ''}
                    onChange={handleFormChange}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  >
                    <option value="">Select expense</option>
                    {expenseOptions.map((ex) => (
                      <option key={ex.Id ?? ex.ID} value={ex.Id ?? ex.ID}>
                        {ex.Name || ex.ExpenseName || ex.Title || ex.Id}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {(config.formFields || []).map((f) =>
                f.type === 'checkbox' ? (
                  <div key={f.name} className="flex items-center gap-2 pt-1">
                    <input
                      type="checkbox"
                      name={f.name}
                      checked={!!form[f.name]}
                      onChange={handleFormChange}
                      id={`vm-field-${f.name}`}
                      className="rounded border-slate-300 text-[#46955A] focus:ring-[#46955A]"
                    />
                    <label htmlFor={`vm-field-${f.name}`} className="text-sm text-slate-700">
                      {f.label}
                      {f.required ? ' *' : ''}
                    </label>
                  </div>
                ) : f.type === 'textarea' ? (
                  <div key={f.name}>
                    <label className="block text-xs font-medium text-slate-500 mb-1">
                      {f.label}
                      {f.required ? ' *' : ''}
                    </label>
                    <textarea
                      name={f.name}
                      value={form[f.name] ?? ''}
                      onChange={handleFormChange}
                      rows={f.rows || 3}
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm min-h-[4.5rem] resize-y focus:border-[#46955A] focus:ring-1 focus:ring-[#46955A]/30 focus:outline-none"
                    />
                  </div>
                ) : (
                  <div key={f.name}>
                    <label className="block text-xs font-medium text-slate-500 mb-1">
                      {f.label}
                      {f.required ? ' *' : ''}
                    </label>
                    <input
                      type={f.type || 'text'}
                      name={f.name}
                      value={form[f.name] ?? ''}
                      onChange={handleFormChange}
                      step={f.step}
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-[#46955A] focus:ring-1 focus:ring-[#46955A]/30 focus:outline-none"
                    />
                  </div>
                )
              )}
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="px-4 py-2 rounded-lg border border-[#46955A] text-[#46955A] hover:bg-[#f0f9f4]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#46955A] text-white hover:bg-[#3d7d4f] disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleEntityGridPage;
